import {
  Close,
  ErrorOutlined,
  InfoOutlined,
  WarningAmber,
} from '@mui/icons-material';
import {
  AppBar,
  Container,
  Icon,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { neutralColors, systemColors } from '../theme/colors';

interface SystemBannerProps {
  message: string;
  type?: 'error' | 'warning' | 'info';
  dismissible?: boolean;
  onDismiss?: () => void;
  customTitle?: string;
}

function SystemBanner({
  message,
  type = 'error',
  dismissible = false,
  onDismiss,
  customTitle,
}: SystemBannerProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const getBannerConfig = () => {
    switch (type) {
      case 'warning':
        return {
          borderColor: systemColors.warningYellow,
          icon: WarningAmber,
          title: customTitle || 'System Notice',
          textColor: neutralColors.white,
        };
      case 'info':
        return {
          borderColor: systemColors.infoBlue,
          icon: InfoOutlined,
          title: customTitle || 'System Update',
          textColor: neutralColors.white,
        };
      default: // error
        return {
          borderColor: systemColors.errorRed,
          icon: ErrorOutlined,
          title: customTitle || 'System Maintenance',
          textColor: neutralColors.white,
        };
    }
  };

  const config = getBannerConfig();

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  return (
    <AppBar
      variant="message"
      position="sticky"
      role="banner"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      sx={{
        borderBottom: `3px solid ${config.borderColor}`,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        animation: 'bannerFadeIn 0.2s ease-out',
        '@keyframes bannerFadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: '1320px' }}>
        <Toolbar disableGutters sx={{ minHeight: 'auto', py: 1 }}>
          <div className="d-flex flex-grow-1 align-items-center justify-content-between">
            <div
              className="d-flex align-items-center gap-2"
              style={{ minHeight: 0, flex: 1 }}
            >
              <Icon
                sx={{
                  color: config.borderColor,
                  fontSize: '24px',
                  mr: 1.5,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  animation: type === 'error' ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.6,
                    },
                    '100%': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <config.icon />
              </Icon>
              <div style={{ minWidth: 0, flex: 1 }}>
                <Typography
                  color={config.textColor}
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    mb: 0.5,
                    lineHeight: 1.2,
                  }}
                >
                  {config.title}
                </Typography>
                <Typography
                  color={config.textColor}
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    lineHeight: 1.3,
                    wordWrap: 'break-word',
                    flex: 1,
                  }}
                >
                  {message}
                </Typography>
              </div>
            </div>
            <div
              className="d-flex align-items-center gap-2"
              style={{ flexShrink: 0 }}
            >
              {dismissible && (
                <IconButton
                  onClick={handleDismiss}
                  size="small"
                  aria-label="Dismiss message"
                  sx={{
                    color: neutralColors.white,
                    opacity: 0.7,
                    transition:
                      'opacity 0.15s ease, background-color 0.15s ease',
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </div>
          </div>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default SystemBanner;
